import {
	Accordion,
	AccordionHeader,
	AccordionItem,
	AccordionPanel,
	mergeClasses,
	Table,
	TableBody,
	TableCell,
	TableCellLayout,
	TableRow,
	Text,
	Spinner,
} from "@fluentui/react-components";
import { StatisticsPanelProps } from "./types";
import { Flex } from "@/components/atoms/flex";
import { useShadowStyle } from "@/common/theme/shadowStyles";
import { useCallback, useMemo } from "react";
import { shadowByPosition } from "@/common/theme/styleUtils";
import { useStyle } from "./styles";
import { PanelHeader } from "@/components/atoms/panelHeader";
import { useIntl } from "react-intl";
import { useQueuesStore } from "@/common/stores/nonpersistingStores/queues";
import { useQuery } from "@tanstack/react-query";
import { fetchQueueStatistics } from "@/repository/queryFns/statistics";
import { QueueStatistics } from "@/repository/models/statistics";
import { QueueType } from "@/repository/models";

export const StatisticsPanel = (props: StatisticsPanelProps) => {
	const { shadowTo } = props;

	const intl = useIntl();

	const classes = useStyle();

	const shadowStyle = useShadowStyle();

	const shadow = useMemo<string | undefined>(
		() => shadowByPosition(shadowTo, shadowStyle),
		[shadowTo, shadowStyle]
	);

	const queues = useQueuesStore((state) => state.queues);
	const allQueues = useMemo(
		() => [...queues.filter((q) => q.type === QueueType.Voice)],
		[queues]
	);

	const {
		data: statisticsData,
		isLoading,
		error,
	} = useQuery<QueueStatistics[]>({
		queryKey: ["queueStatistics", allQueues.map((q) => q.id)],
		queryFn: () =>
			fetchQueueStatistics({ queueIds: allQueues.map((q) => q.id) }),
		enabled: allQueues.length > 0,
	});

	const formatStatisticLabel = useCallback(
		(key: string): string => {
			switch (key) {
				case "totHandlingSec":
					return intl.formatMessage({ id: "Statistics.TotalHandlingSeconds" });
				case "avgHandlingSec":
					return intl.formatMessage({
						id: "Statistics.AverageHandlingSeconds",
					});
				case "totWaitSec":
					return intl.formatMessage({ id: "Statistics.TotalWaitSeconds" });
				case "avgWaitSec":
					return intl.formatMessage({ id: "Statistics.AverageWaitSeconds" });
				case "inboundCalls":
					return intl.formatMessage({ id: "Statistics.InboundCalls" });
				case "inboundHandled":
					return intl.formatMessage({ id: "Statistics.InboundHandled" });
				default:
					return key;
			}
		},
		[intl]
	);

	const formatStatisticValue = useCallback(
		(value: number, key: string): string => {
			if (key.includes("Sec")) {
				const roundedValue = Math.round(value);
				if (roundedValue >= 60) {
					const minutes = Math.floor(roundedValue / 60);
					const seconds = roundedValue % 60;
					return `${minutes}m ${seconds}s`;
				}
				return `${roundedValue}s`;
			}
			return value.toString();
		},
		[]
	);

	const buildStatisticsAccordionItems = useCallback(() => {
		return allQueues.map((queue, index) => {
			const queueStats = statisticsData?.find(
				(stat) => stat.queueId === queue.id
			);

			const statisticsToDisplay: (keyof QueueStatistics)[] = [
				"totHandlingSec",
				"avgHandlingSec",
				"totWaitSec",
				"avgWaitSec",
				"inboundCalls",
				"inboundHandled",
			];

			return (
				<AccordionItem key={queue.id} value={index.toString()}>
					<AccordionHeader className={classes.accordionHeader}>
						<Text>{queue.name}</Text>
					</AccordionHeader>
					<AccordionPanel className={classes.accordionPanel}>
						<Table>
							<TableBody>
								{statisticsToDisplay.map((statKey) => (
									<TableRow key={statKey}>
										<TableCell>
											<TableCellLayout>
												{formatStatisticLabel(statKey)}
											</TableCellLayout>
										</TableCell>
										<TableCell>
											<TableCellLayout className={classes.accordionValue}>
												{queueStats
													? formatStatisticValue(
															queueStats[statKey] as number,
															statKey
														)
													: "-"}
											</TableCellLayout>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</AccordionPanel>
				</AccordionItem>
			);
		});
	}, [
		allQueues,
		statisticsData,
		error,
		classes.accordionHeader,
		classes.accordionPanel,
		formatStatisticLabel,
		formatStatisticValue,
	]);

	return (
		<Flex column className={mergeClasses(classes.container, shadow)}>
			<PanelHeader
				left={
					<Text className={classes.headerTitle}>
						{intl.formatMessage({ id: "Statistics.HeaderTitle" })}
					</Text>
				}
			/>
			{isLoading && (
				<Flex
					column
					vAlign="center"
					hAlign="center"
					style={{ padding: "32px" }}
				>
					<Spinner size="medium" />
				</Flex>
			)}
			{error && (
				<Flex
					column
					vAlign="center"
					hAlign="center"
					style={{ padding: "32px" }}
				>
					<Text>Statistics not available</Text>
				</Flex>
			)}
			{!isLoading && !error && (
				<Accordion multiple collapsible className={classes.accordion}>
					{buildStatisticsAccordionItems()}
				</Accordion>
			)}
		</Flex>
	);
};
